import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { useFormik } from 'formik';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from '../../../hooks/useTranslation';
import MlxButton from '../../../components/common/MlxButton/MlxButton';
import MlxBlockUI from '../../../components/common/MlxBlockUI/MlxBlockUI';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import MlxBadge from '../../../components/common/MlxBadge/MlxBadge';
import { _AddSubscriptionPlanFormType, _ToastMessageProps } from '../../../constants/staticTypes';
import { AlertVariant } from '../../../constants/constants';
import {
    addSubscriptionPlanValidationSchema,
    initialAddSubscriptionPlanValues,
    planDurationOptions,
    _FeatureForPlan,
    _SubscriptionPlanFromAPI,
    updateSubscriptionPlan,
    createSubscriptionPlan,
    createAddonPlan,
    updateAddonPlan,
    _AddonPlanFromAPI,
    _UpdateAddonRequest,
    _CreateAddonRequest,
} from '../../../helpers/subscriptionHelper';
import FeatureCustomization, { FeatureConfiguration } from '../add-new-plan/components/FeatureCustomization';
import { useQueryClient, useMutation } from 'react-query';
import { SUBSCRIPTION_PLANS_LIST, ADDON_PLANS_LIST } from '../../../constants/queryKeys';
import logger from '../../../utils/logger';
import './CreatePlan.scss';

type Props = {
    onCancel: () => void;
    isAddOn?: boolean;
    availableFeatures: _FeatureForPlan[];
    featuresLoading: boolean;
    editData?: _SubscriptionPlanFromAPI | _AddonPlanFromAPI | null;
    setToastMessage: React.Dispatch<React.SetStateAction<_ToastMessageProps>>;
    setIsSaving?: (loading: boolean) => void;
};

export type CreatePlanRef = {
    handleReset: () => void;
    handleSave: () => void;
};

const languageTexts = {
    ADD_SUBSCRIPTION_PLAN: 'Add Subscription Plan',
    ADD_ADD_ON: 'Add Add-On',
    ADDON_DETAILS: 'Add-on Details',
    ADDON_DETAILS_DESC: 'Configure plan information and appearance',
    ADDON_NAME: 'Add-On Name',
    ADDON_BENEFIT: 'Add-On Benefit',
    ADD_YOUR_ADDON_BENEFIT: 'Add your Add-on benefit',
    PLAN_DETAILS: 'Plan Details',
    PLAN_DETAILS_DESC: 'Configure plan information and appearance',
    IS_ADD_ON: 'Is this an Add-On?',
    ADD_ON_DESC: 'This Add-On provides additional feature that is not included in the standard plan.',
    PLAN_NAME: 'Plan Name',
    PLAN_ICON: 'Plan Icon',
    DESCRIPTION: 'Description',
    ACTIVE_STATUS: 'Active Status',
    ACTIVE_STATUS_DESC: 'Make this plan available for new subscriptions',
    MAKE_ADDON_AVAILABLE: 'Make this add-on available for new subscriptions',
    SET_RECOMMENDED: 'Set this plan as Recommended',
    FEATURE_CUSTOMIZATION: 'Feature Customization',
    FEATURE_CUSTOMIZATION_DESC: 'Add features and configure their limits',
    SELECT_FEATURE: 'Select Feature',
    ADDED_FEATURES: 'Added Features',
    CONFIGURE_LIMITS: 'Configure limits for each feature',
    PLAN_BENEFIT: 'Plan Benefit',
    ADD_BENEFIT: 'Add your plan benefit',
    PRICING_OPTIONS: 'Pricing Options',
    PRICING_DESC: 'Configure billing periods and prices',
    BILLING_PERIOD: 'Billing Period',
    PRICE: 'Price',
    ADD: 'Add',
    CANCEL: 'Cancel',
    CREATE_PLAN: 'Create Plan',
    CREATE_ADDON: 'Create Add-On',
    FEATURES: 'Features',
    PRICING: 'Pricing',
    NO_FEATURES: 'No features',
};

// Interval mapping constants for pricing options
const INTERVAL_MAP: { [key: string]: string } = {
    Monthly: 'month',
    Yearly: 'year',
    Quarterly: 'quarter',
};

const INTERVAL_COUNT_MAP: { [key: string]: number } = {
    Monthly: 1,
    Yearly: 1,
    Quarterly: 3,
};

const CreatePlan = forwardRef<CreatePlanRef, Props>(
    ({ onCancel, isAddOn = false, availableFeatures, featuresLoading, editData, setToastMessage, setIsSaving }, ref) => {
        const translatedText = useTranslation(languageTexts, 'capitalize');
        const [isAddOnInternal, setIsAddOnInternal] = useState(isAddOn);
        const [activeStatus, setActiveStatus] = useState(true);
        const [selectedIcon, setSelectedIcon] = useState('ri-gift-line');
        const [benefits, setBenefits] = useState<string[]>([]);
        const [newBenefit, setNewBenefit] = useState('');
        const [pricingOptions, setPricingOptions] = useState<
            Array<{ id: number; period: string; price: number; originalPriceId?: string; originalPrice?: number; originalPeriod?: string }>
        >([{ id: 1, period: 'Monthly', price: 0.0 }]);
        const [pricingErrors, setPricingErrors] = useState<{ [key: number]: string }>({}); // Track errors for each pricing option
        const [addedFeatures, setAddedFeatures] = useState<FeatureConfiguration[]>([]);
        const [initialEditData, setInitialEditData] = useState<_SubscriptionPlanFromAPI | _AddonPlanFromAPI | null>(null);
        const [initialFeatures, setInitialFeatures] = useState<FeatureConfiguration[]>([]);
        const queryClient = useQueryClient();

        const updatePlanMutation = useMutation((data: any) => updateSubscriptionPlan(queryClient, data), {
            onMutate: () => {
                setIsSaving?.(true);
            },
            onSuccess: (response: any) => {
                setIsSaving?.(false);
                queryClient.invalidateQueries([SUBSCRIPTION_PLANS_LIST]);
                setToastMessage({
                    show: true,
                    message: response?.message || 'Subscription plan updated successfully',
                    variant: AlertVariant.SUCCESS,
                });
                onCancel();
            },
            onError: (error: any) => {
                setIsSaving?.(false);
                logger.log('Failed to update plan', error);
                setToastMessage({
                    show: true,
                    message: error?.response?.data?.message || 'Failed to update subscription plan. Please try again.',
                    variant: AlertVariant.ERROR,
                });
            },
        });

        const createPlanMutation = useMutation((data: any) => createSubscriptionPlan(queryClient, data), {
            onMutate: () => {
                setIsSaving?.(true);
            },
            onSuccess: (response: any) => {
                setIsSaving?.(false);
                queryClient.invalidateQueries([SUBSCRIPTION_PLANS_LIST]);
                setToastMessage({
                    show: true,
                    message: response?.message || 'Subscription plan created successfully',
                    variant: AlertVariant.SUCCESS,
                });
                onCancel();
            },
            onError: (error: any) => {
                setIsSaving?.(false);
                logger.log('Failed to create plan', error);
                setToastMessage({
                    show: true,
                    message: error?.response?.data?.message || 'Failed to create subscription plan. Please try again.',
                    variant: AlertVariant.ERROR,
                });
            },
        });

        const createAddonMutation = useMutation((data: _CreateAddonRequest) => createAddonPlan(queryClient, data), {
            onMutate: () => {
                setIsSaving?.(true);
            },
            onSuccess: (response: any) => {
                setIsSaving?.(false);
                queryClient.invalidateQueries([ADDON_PLANS_LIST]);
                setToastMessage({
                    show: true,
                    message: response?.message || 'Addon created successfully',
                    variant: AlertVariant.SUCCESS,
                });
                onCancel();
            },
            onError: (error: any) => {
                setIsSaving?.(false);
                logger.log('Failed to create addon', error);
                setToastMessage({
                    show: true,
                    message: error?.response?.data?.message || 'Failed to create addon. Please try again.',
                    variant: AlertVariant.ERROR,
                });
            },
        });

        const updateAddonMutation = useMutation((data: _UpdateAddonRequest) => updateAddonPlan(queryClient, data), {
            onMutate: () => {
                setIsSaving?.(true);
            },
            onSuccess: (response: any) => {
                setIsSaving?.(false);
                queryClient.invalidateQueries([ADDON_PLANS_LIST]);
                setToastMessage({
                    show: true,
                    message: response?.message || 'Addon updated successfully',
                    variant: AlertVariant.SUCCESS,
                });
                onCancel();
            },
            onError: (error: any) => {
                setIsSaving?.(false);
                logger.log('Failed to update addon', error);
                setToastMessage({
                    show: true,
                    message: error?.response?.data?.message || 'Failed to update addon. Please try again.',
                    variant: AlertVariant.ERROR,
                });
            },
        });

        const availableIcons = [
            { icon: 'ri-gift-line', color: '#6c757d', bg: '#E8E8E8' },
            { icon: 'ri-star-line', color: '#F57C00', bg: '#FFF4E6' },
            { icon: 'ri-flashlight-line', color: '#0288D1', bg: '#E8F4FD' },
            { icon: 'ri-vip-crown-line', color: '#F9A825', bg: '#FFF9E6' },
        ];

        const formik = useFormik({
            initialValues: initialAddSubscriptionPlanValues,
            enableReinitialize: true,
            validationSchema: addSubscriptionPlanValidationSchema,
            onSubmit: (values) => {
                // TODO: Implement API call to save subscription plan
                onCancel();
            },
        });

        const handleAddBenefit = () => {
            if (newBenefit.trim()) {
                setBenefits([...benefits, newBenefit.trim()]);
                setNewBenefit('');
            }
        };

        const handleRemoveBenefit = (index: number) => {
            setBenefits(benefits.filter((_, i) => i !== index));
        };

        const handleAddPricingOption = () => {
            const newId = pricingOptions.length + 1;
            setPricingOptions([...pricingOptions, { id: newId, period: 'Yearly', price: 0.0 }]);
        };

        const handleRemovePricingOption = (id: number) => {
            setPricingOptions(pricingOptions.filter((option) => option.id !== id));
        };

        const handlePricingChange = (id: number, field: string, value: any) => {
            setPricingOptions(pricingOptions.map((option) => (option.id === id ? { ...option, [field]: value } : option)));

            // Clear error for this pricing option when price is updated
            if (field === 'price' && value > 0) {
                setPricingErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[id];
                    return newErrors;
                });
            }
        };

        const handleAddFeature = (feature: FeatureConfiguration) => {
            setAddedFeatures([...addedFeatures, feature]);
        };

        const handleRemoveFeature = (featureId: string) => {
            setAddedFeatures(addedFeatures.filter((f) => f.featureId !== featureId));
        };

        const handleUpdateFeatureOption = (featureId: string, optionId: string, field: 'value' | 'unit', value: string) => {
            setAddedFeatures(
                addedFeatures.map((feature) =>
                    feature.featureId === featureId
                        ? {
                              ...feature,
                              options: {
                                  ...feature.options,
                                  [optionId]: {
                                      ...feature.options[optionId],
                                      [field]: value,
                                  },
                              },
                          }
                        : feature
                )
            );
        };

        // Populate form when editing
        useEffect(() => {
            if (editData) {
                // Store initial edit data for reset functionality
                setInitialEditData(editData);

                // Set basic fields
                formik.setFieldValue('planName', editData.name);
                formik.setFieldValue('description', editData.description);
                setActiveStatus(editData.isActive);
                setIsAddOnInternal(editData.isAddon);

                // Set icon from metadata
                if (editData.metadata?.planIconClass) {
                    setSelectedIcon(editData.metadata.planIconClass);
                }

                // Set recommended flag
                if (editData.metadata?.setAsRecommended === 'true') {
                    formik.setFieldValue('isRecommended', true);
                }

                // Set benefits from marketing_features
                if (editData.additionalData?.marketing_features && Array.isArray(editData.additionalData.marketing_features)) {
                    const marketingBenefits = editData.additionalData.marketing_features
                        .map((f: any) => (typeof f === 'string' ? f : f?.name))
                        .filter((f: any) => f); // Filter out null/undefined
                    setBenefits(marketingBenefits);
                }

                // Set pricing options
                if (editData.prices && editData.prices.length > 0) {
                    const prices = editData.prices.map((price, index) => {
                        const period =
                            price.recurring.interval === 'year' ? 'Yearly' : price.recurring.interval === 'month' ? 'Monthly' : 'Monthly';
                        const priceAmount = parseFloat(price.amount);
                        return {
                            id: index + 1,
                            period,
                            price: priceAmount,
                            originalPriceId: price.id,
                            originalPrice: priceAmount, // Store original price for comparison
                            originalPeriod: period, // Store original period for comparison
                        };
                    });
                    setPricingOptions(prices);
                }

                // Set features
                if (editData.planFeatures && editData.planFeatures.length > 0) {
                    const features: FeatureConfiguration[] = [];

                    // Group by feature ID
                    const featureMap = new Map<string, any[]>();
                    editData.planFeatures.forEach((pf) => {
                        if (!featureMap.has(pf.featureId)) {
                            featureMap.set(pf.featureId, []);
                        }
                        featureMap.get(pf.featureId)!.push(pf);
                    });

                    // Convert to FeatureConfiguration
                    featureMap.forEach((planFeatures, featureId) => {
                        const firstPf = planFeatures[0];
                        const featureConfig: FeatureConfiguration = {
                            featureId: featureId,
                            featureName: firstPf.feature.name,
                            options: {},
                        };

                        // Add all options for this feature
                        planFeatures.forEach((pf) => {
                            if (!pf.featureOption) return;
                            const isToggle =
                                pf.featureOption.optionUnitList.includes('enabled') && pf.featureOption.optionUnitList.includes('disabled');

                            featureConfig.options[pf.featureOptionId] = {
                                optionName: pf.featureOption.optionName,
                                optionDescription: pf.featureOption.optionDescription,
                                value: isToggle ? (pf.limitUnit === 'enabled' ? 'enabled' : 'disabled') : pf.limitValue || '10',
                                unit: isToggle ? '' : pf.limitUnit, // Empty string for toggles so value is used
                                availableUnits: pf.featureOption.optionUnitList,
                                planFeatureId: pf.id, // Store the plan-feature link ID
                            };
                        });

                        features.push(featureConfig);
                    });

                    setAddedFeatures(features);
                    setInitialFeatures(features);
                }
            } else {
                // Reset form when creating new
                formik.resetForm();
                setActiveStatus(true);
                setSelectedIcon('ri-gift-line');
                setBenefits([]);
                setPricingOptions([{ id: 1, period: 'Monthly', price: 0.0 }]);
                setAddedFeatures([]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [editData]);

        const handleReset = () => {
            if (initialEditData) {
                // Repopulate form with initial edit data
                formik.setFieldValue('planName', initialEditData.name);
                formik.setFieldValue('description', initialEditData.description);
                setActiveStatus(initialEditData.isActive);
                setIsAddOnInternal(initialEditData.isAddon);

                // Reset icon
                if (initialEditData.metadata?.planIconClass) {
                    setSelectedIcon(initialEditData.metadata.planIconClass);
                }

                // Reset recommended flag
                if (initialEditData.metadata?.setAsRecommended === 'true') {
                    formik.setFieldValue('isRecommended', true);
                } else {
                    formik.setFieldValue('isRecommended', false);
                }

                // Reset benefits
                if (
                    initialEditData.additionalData?.marketing_features &&
                    Array.isArray(initialEditData.additionalData.marketing_features)
                ) {
                    const marketingBenefits = initialEditData.additionalData.marketing_features
                        .map((f: any) => (typeof f === 'string' ? f : f?.name))
                        .filter((f: any) => f); // Filter out null/undefined
                    setBenefits(marketingBenefits);
                } else {
                    setBenefits([]);
                }

                // Reset pricing
                if (initialEditData.prices && initialEditData.prices.length > 0) {
                    const prices = initialEditData.prices.map((price, index) => ({
                        id: index + 1,
                        period:
                            price.recurring.interval === 'year' ? 'Yearly' : price.recurring.interval === 'month' ? 'Monthly' : 'Monthly',
                        price: parseFloat(price.amount),
                        originalPriceId: price.id, // Store the original price ID
                    }));
                    setPricingOptions(prices);
                } else {
                    setPricingOptions([{ id: 1, period: 'Monthly', price: 0.0 }]);
                }

                // Reset features
                if (initialEditData.planFeatures && initialEditData.planFeatures.length > 0) {
                    const features: FeatureConfiguration[] = [];

                    const featureMap = new Map<string, any[]>();
                    initialEditData.planFeatures.forEach((pf) => {
                        if (!featureMap.has(pf.featureId)) {
                            featureMap.set(pf.featureId, []);
                        }
                        featureMap.get(pf.featureId)!.push(pf);
                    });

                    featureMap.forEach((planFeatures, featureId) => {
                        const firstPf = planFeatures[0];
                        const featureConfig: FeatureConfiguration = {
                            featureId: featureId,
                            featureName: firstPf.feature.name,
                            options: {},
                        };

                        planFeatures.forEach((pf) => {
                            if (!pf.featureOption) return;
                            const isToggle =
                                pf.featureOption.optionUnitList.includes('enabled') && pf.featureOption.optionUnitList.includes('disabled');

                            featureConfig.options[pf.featureOptionId] = {
                                optionName: pf.featureOption.optionName,
                                optionDescription: pf.featureOption.optionDescription,
                                value: isToggle ? (pf.limitUnit === 'enabled' ? 'enabled' : 'disabled') : pf.limitValue || '10',
                                unit: pf.limitUnit,
                                availableUnits: pf.featureOption.optionUnitList,
                                planFeatureId: pf.id, // Store the plan-feature link ID
                            };
                        });

                        features.push(featureConfig);
                    });

                    setAddedFeatures(features);
                } else {
                    setAddedFeatures([]);
                }
            }
        };

        const handleSave = async () => {
            // Validation before submitting - Trigger formik validation
            const validationErrors = await formik.validateForm();

            if (validationErrors.planName || validationErrors.description) {
                // Mark fields as touched to show errors
                formik.setTouched({
                    planName: true,
                    description: true,
                });
                return;
            }

            // Validate pricing options
            if (pricingOptions.length === 0) {
                setToastMessage({
                    show: true,
                    message: 'At least one pricing option is required',
                    variant: AlertVariant.ERROR,
                });
                return;
            }

            // Validate prices and set inline errors
            const errors: { [key: number]: string } = {};
            pricingOptions.forEach((option) => {
                if (!option.price || option.price <= 0) {
                    errors[option.id] = 'Price must be greater than $0';
                }
            });

            if (Object.keys(errors).length > 0) {
                setPricingErrors(errors);
                return;
            }

            // Clear any previous errors
            setPricingErrors({});

            // Check if we're in edit mode or create mode
            const isEditMode = !!editData;

            if (isEditMode) {
                // UPDATE MODE
                // Determine which prices are unchanged, edited, or new
                const existingPriceIds: string[] = [];
                const newPrices: any[] = [];

                pricingOptions.forEach((option) => {
                    // Check if this price has an originalPriceId (meaning it already existed)
                    if (option.originalPriceId) {
                        // Check if the price or period has changed
                        const priceChanged = option.price !== option.originalPrice;
                        const periodChanged = option.period !== option.originalPeriod;

                        if (priceChanged || periodChanged) {
                            // Price was edited - add to newPrices
                            newPrices.push({
                                amount: option.price,
                                currency: 'USD',
                                interval: INTERVAL_MAP[option.period] || 'month',
                                type: 'recurring',
                                intervalCount: INTERVAL_COUNT_MAP[option.period] || 1,
                                metadata: {},
                            });
                        } else {
                            // Price unchanged - keep in activePriceIds
                            existingPriceIds.push(option.originalPriceId);
                        }
                    } else {
                        // This is a completely new price - add to newPrices
                        newPrices.push({
                            amount: option.price,
                            currency: 'USD',
                            interval: INTERVAL_MAP[option.period] || 'month',
                            type: 'recurring',
                            intervalCount: INTERVAL_COUNT_MAP[option.period] || 1,
                            metadata: {},
                        });
                    }
                });

                // Determine feature changes
                const linkedFeatures: any[] = [];
                const removedFeatureIds: string[] = [];

                // Check for removed features
                initialFeatures.forEach((initialFeature) => {
                    if (!addedFeatures.find((f) => f.featureId === initialFeature.featureId)) {
                        // Feature was removed, collect all plan-feature link IDs from its options
                        Object.values(initialFeature.options).forEach((option) => {
                            if (option.planFeatureId) {
                                removedFeatureIds.push(option.planFeatureId);
                            }
                        });
                    }
                });

                // Check for new or modified features
                // For update API: send ALL current features with planFeatureId for existing ones
                addedFeatures.forEach((feature) => {
                    const initialFeature = initialFeatures.find((f) => f.featureId === feature.featureId);

                    // Add all options for this feature
                    Object.entries(feature.options).forEach(([optionId, option]) => {
                        const linkedFeature: any = {
                            featureId: feature.featureId,
                            featureOptionId: optionId,
                            featureOptionUnit: option.unit || option.value,
                            isUnlimited: option.unit === 'unlimited' || option.value === 'unlimited',
                        };

                        // Include planFeatureId if this option already existed
                        if (option.planFeatureId) {
                            linkedFeature.planFeatureId = option.planFeatureId;
                        }

                        linkedFeatures.push(linkedFeature);
                    });
                });

                // Prepare request body for UPDATE
                const requestBody: any = {
                    id: editData.id,
                    name: formik.values.planName,
                    description: formik.values.description,
                    isActive: activeStatus,
                    setAsRecommended: formik.values.isRecommended,
                    planIconClass: selectedIcon,
                    marketingFeatures: benefits,
                    activePriceIds: existingPriceIds,
                };

                // Only add newPrices if there are new prices
                if (newPrices.length > 0) {
                    requestBody.newPrices = newPrices;
                }

                // Only add linkedFeatures if there are changes
                if (linkedFeatures.length > 0) {
                    requestBody.linkedFeatures = linkedFeatures;
                }

                // Only add removedPlanFeatureIds if there are removed features
                if (removedFeatureIds.length > 0) {
                    requestBody.removedPlanFeatureIds = removedFeatureIds;
                }

                // Call the UPDATE API - use addon API for addons, regular API for plans
                if (isAddOnInternal) {
                    updateAddonMutation.mutate(requestBody);
                } else {
                    updatePlanMutation.mutate(requestBody);
                }
            } else {
                // CREATE MODE
                // Prepare prices array for create
                const prices = pricingOptions.map((option) => {
                    return {
                        amount: option.price,
                        currency: 'USD',
                        interval: INTERVAL_MAP[option.period] || 'month',
                        type: 'recurring' as 'recurring' | 'one_time',
                        intervalCount: INTERVAL_COUNT_MAP[option.period] || 1,
                        metadata: {
                            displayName: option.period,
                            recommended: formik.values.isRecommended,
                        },
                    };
                });

                // Prepare linked features for create
                const linkedFeatures: any[] = [];
                addedFeatures.forEach((feature) => {
                    Object.entries(feature.options).forEach(([optionId, option]) => {
                        linkedFeatures.push({
                            featureId: feature.featureId,
                            featureOptionId: optionId,
                            featureOptionUnit: option.unit || option.value,
                            isUnlimited: option.unit === 'unlimited' || option.value === 'unlimited',
                        });
                    });
                });

                // Prepare request body for CREATE
                let requestBody: _CreateAddonRequest | any; // Use union type for flexibility

                if (isAddOnInternal) {
                    // Addon API has a different structure - only send required fields
                    const addonBody: Partial<_CreateAddonRequest> = {
                        name: formik.values.planName,
                        description: formik.values.description,
                        marketingFeatures: benefits,
                        prices: prices,
                    };

                    // Only add linkedFeatures if there are features
                    if (linkedFeatures.length > 0) {
                        addonBody.linkedFeatures = linkedFeatures;
                    }

                    requestBody = addonBody as _CreateAddonRequest;
                } else {
                    // Plan API includes additional fields
                    requestBody = {
                        name: formik.values.planName,
                        description: formik.values.description,
                        isActive: activeStatus,
                        setAsRecommended: formik.values.isRecommended,
                        planIconClass: selectedIcon,
                        marketingFeatures: benefits,
                        prices: prices,
                    };

                    // Only add linkedFeatures if there are features for plans
                    if (linkedFeatures.length > 0) {
                        requestBody.linkedFeatures = linkedFeatures;
                    }
                }

                // Call the CREATE API - use addon API for addons, regular API for plans
                if (isAddOnInternal) {
                    createAddonMutation.mutate(requestBody);
                } else {
                    createPlanMutation.mutate(requestBody);
                }
            }
        };

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            handleReset,
            handleSave,
        }));

        return (
            <div className="">
                {/* <div className="page-header-with-back d-flex align-items-center mt-2">
                <MlxButton rounded text size="large" icon="ri-arrow-left-line" onClick={onCancel} className="text-dark me-2" />
                <span className="fw-bold">{isAddOnInternal ? translatedText.ADD_ADD_ON : translatedText.ADD_SUBSCRIPTION_PLAN}</span>
            </div> */}
                <div className="create-plan-page p-3 pt-2">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            {/* Left Column - Plan Details */}
                            <div className="col-lg-6">
                                {/* Plan Details Section */}
                                <div className="form-section">
                                    <h6 className="section-title">
                                        {isAddOnInternal ? translatedText.ADDON_DETAILS : translatedText.PLAN_DETAILS}
                                    </h6>
                                    <p className="section-subtitle">
                                        {isAddOnInternal ? translatedText.ADDON_DETAILS_DESC : translatedText.PLAN_DETAILS_DESC}
                                    </p>

                                    {/* Is this an Add-On - Only show when not passed as prop */}
                                    {!isAddOn && (
                                        <div className="addon-toggle mb-4">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <label className="form-label mb-1">{translatedText.IS_ADD_ON}</label>
                                                    <p className="text-muted small mb-0">{translatedText.ADD_ON_DESC}</p>
                                                </div>
                                                <InputSwitch checked={isAddOnInternal} onChange={(e) => setIsAddOnInternal(e.value)} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Plan/Add-on Name */}
                                    <div className="mb-3">
                                        <label htmlFor="planName" className="form-label">
                                            {isAddOnInternal ? translatedText.ADDON_NAME : translatedText.PLAN_NAME}
                                        </label>
                                        <InputText
                                            id="planName"
                                            name="planName"
                                            value={formik.values.planName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`w-100 ${formik.touched.planName && formik.errors.planName ? 'p-invalid' : ''}`}
                                            autoComplete="off"
                                        />
                                        {formik.touched.planName && formik.errors.planName && (
                                            <small className="p-error">{formik.errors.planName}</small>
                                        )}
                                    </div>

                                    {/* Plan Icon - Only show for subscription plans */}
                                    {!isAddOnInternal && (
                                        <div className="mb-3">
                                            <label className="form-label">{translatedText.PLAN_ICON}</label>
                                            <div className="icon-selector">
                                                {availableIcons.map((iconItem, index) => (
                                                    <div
                                                        key={index}
                                                        className={`icon-option ${selectedIcon === iconItem.icon ? 'selected' : ''}`}
                                                        onClick={() => setSelectedIcon(iconItem.icon)}
                                                        style={{
                                                            backgroundColor: iconItem.bg,
                                                            color: iconItem.color,
                                                        }}
                                                    >
                                                        <i className={iconItem.icon}></i>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Description */}
                                    <div className="mb-3">
                                        <label htmlFor="description" className="form-label">
                                            {translatedText.DESCRIPTION}
                                        </label>
                                        <InputText
                                            id="description"
                                            name="description"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`w-100 ${formik.touched.description && formik.errors.description ? 'p-invalid' : ''}`}
                                            autoComplete="off"
                                        />
                                    </div>

                                    {/* Active Status */}
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <label className="form-label mb-1">{translatedText.ACTIVE_STATUS}</label>
                                                <p className="text-muted small mb-0">
                                                    {isAddOnInternal
                                                        ? translatedText.MAKE_ADDON_AVAILABLE
                                                        : translatedText.ACTIVE_STATUS_DESC}
                                                </p>
                                            </div>
                                            <InputSwitch checked={activeStatus} onChange={(e) => setActiveStatus(e.value)} />
                                        </div>
                                    </div>

                                    {/* Set as Recommended - Only show for subscription plans */}
                                    {!isAddOnInternal && (
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center">
                                                <Checkbox
                                                    inputId="isRecommended"
                                                    name="isRecommended"
                                                    checked={formik.values.isRecommended}
                                                    onChange={(e) => formik.setFieldValue('isRecommended', e.checked)}
                                                />
                                                <label htmlFor="isRecommended" className="ms-2 mb-0">
                                                    {translatedText.SET_RECOMMENDED}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Feature Customization Section */}
                                <div className="form-section mt-4">
                                    <MlxBlockUI isLoading={featuresLoading} opacity={0.3}>
                                        <FeatureCustomization
                                            availableFeatures={availableFeatures}
                                            addedFeatures={addedFeatures}
                                            onAddFeature={handleAddFeature}
                                            onRemoveFeature={handleRemoveFeature}
                                            onUpdateFeatureOption={handleUpdateFeatureOption}
                                        />
                                    </MlxBlockUI>
                                </div>
                            </div>

                            {/* Right Column - Plan Benefit & Pricing */}
                            <div className="col-lg-6">
                                {/* Plan/Add-On Benefit Section */}
                                <div className="form-section">
                                    <h6 className="section-title">
                                        {isAddOnInternal ? translatedText.ADDON_BENEFIT : translatedText.PLAN_BENEFIT}
                                    </h6>

                                    {/* Add Benefit Input */}
                                    <div className="mb-3">
                                        <div className="d-flex gap-2">
                                            <InputText
                                                value={newBenefit}
                                                onChange={(e) => setNewBenefit(e.target.value)}
                                                className="flex-grow-1"
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddBenefit();
                                                    }
                                                }}
                                            />
                                            <MlxButton
                                                icon="ri-check-line"
                                                onClick={handleAddBenefit}
                                                disabled={!newBenefit.trim()}
                                                className="add-benefit-btn"
                                            />
                                        </div>
                                    </div>

                                    {/* Benefits List */}
                                    {benefits.length > 0 && (
                                        <div className="benefits-list">
                                            {benefits.map((benefit, index) => (
                                                <div key={index} className="benefit-item">
                                                    <i className="ri-checkbox-circle-fill text-success"></i>
                                                    <span className="benefit-text">{benefit}</span>
                                                    <i
                                                        className="ri-close-line text-danger cursor-pointer"
                                                        onClick={() => handleRemoveBenefit(index)}
                                                    ></i>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Pricing Options Section */}
                                <div className="form-section mt-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h6 className="section-title mb-1">{translatedText.PRICING_OPTIONS}</h6>
                                            <p className="section-subtitle mb-0">{translatedText.PRICING_DESC}</p>
                                        </div>
                                        <MlxButton
                                            label={translatedText.ADD}
                                            icon="ri-add-line"
                                            size="small"
                                            onClick={handleAddPricingOption}
                                            className="add-pricing-btn"
                                        />
                                    </div>

                                    {/* Pricing Options List */}
                                    {pricingOptions.map((option, index) => (
                                        <div key={option.id} className="pricing-option">
                                            <div className="pricing-header">
                                                <span className="pricing-label">Pricing Option {index + 1}</span>
                                                {pricingOptions.length > 1 && (
                                                    <i
                                                        className="ri-delete-bin-line text-danger cursor-pointer"
                                                        onClick={() => handleRemovePricingOption(option.id)}
                                                    ></i>
                                                )}
                                            </div>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{translatedText.BILLING_PERIOD}</label>
                                                    <Dropdown
                                                        value={option.period}
                                                        options={planDurationOptions}
                                                        onChange={(e) => handlePricingChange(option.id, 'period', e.value)}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        className="w-100"
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">{translatedText.PRICE}</label>
                                                    <div className="p-inputgroup">
                                                        <span className="p-inputgroup-addon">$</span>
                                                        <InputNumber
                                                            value={option.price}
                                                            onValueChange={(e) => handlePricingChange(option.id, 'price', e.value)}
                                                            mode="decimal"
                                                            className={pricingErrors[option.id] ? 'p-invalid' : ''}
                                                            minFractionDigits={2}
                                                            maxFractionDigits={2}
                                                            min={0}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    {pricingErrors[option.id] && (
                                                        <small className="p-error d-block mt-1">{pricingErrors[option.id]}</small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Preview Card */}
                                <div className="preview-card mt-4">
                                    <div className="preview-header">
                                        <div className="d-flex align-items-center">
                                            {!isAddOnInternal && (
                                                <div
                                                    className="plan-icon-preview"
                                                    style={{
                                                        backgroundColor:
                                                            availableIcons.find((i) => i.icon === selectedIcon)?.bg || '#E8E8E8',
                                                        color: availableIcons.find((i) => i.icon === selectedIcon)?.color || '#6c757d',
                                                    }}
                                                >
                                                    <i className={selectedIcon}></i>
                                                </div>
                                            )}
                                            <span className="plan-name-preview">
                                                {formik.values.planName || (isAddOnInternal ? 'F&B MENU' : 'BRONZE PLAN')}
                                            </span>
                                        </div>
                                        <MlxBadge value="Active" severity="success" />
                                    </div>
                                    <div className="preview-body">
                                        <p className="preview-description">
                                            {formik.values.description ||
                                                'Lorem ipsum is simply dummy text of the printing and typesetting industry.'}
                                        </p>
                                        {benefits.length > 0 && (
                                            <>
                                                <label className="preview-features-label">
                                                    {isAddOnInternal ? translatedText.FEATURES : `Features (${benefits.length})`}
                                                </label>
                                                <div className="preview-features">
                                                    {benefits.slice(0, 3).map((benefit, index) => (
                                                        <span key={index} className="feature-badge">
                                                            {benefit}
                                                        </span>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        {pricingOptions.length > 0 && (
                                            <div className="preview-pricing">
                                                <label className="preview-pricing-label">{translatedText.PRICING}</label>
                                                <div className="pricing-badges">
                                                    {pricingOptions.map((option, index) => (
                                                        <span key={index} className="pricing-badge">
                                                            ${option.price?.toFixed(0) ?? '0'}/{option.period === 'Monthly' ? 'mo' : 'yr'}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
);

export default CreatePlan;
